import React from 'react'
import PropTypes from 'prop-types'
import { Notification, Pagination, trackEvent } from '@offerzen/pinata-shame'

import EmptyListCard from '../EmptyListCard'
import { JobListEmptyState } from '../constants'

import useFetchRecommendedJobs from './hooks/useFetchRecommendedJobs'
import useJobSearchState from './hooks/useJobSearchState'
import JobCard from './JobCard'
import JobSearch from './JobSearch'
import SkeletonJobList from './SkeletonJobList'
import styles from './styles'

const JobList = ({ candidateProfileId }) => {
  const { searchTerms, setSearchTerms } = useJobSearchState()

  const { data, isLoading, isFetchError, isPaginationError, currentPage, setPage, paginationOffset, paginationLimit, totalPages } =
    useFetchRecommendedJobs({
      candidateProfileId,
      searchTerms,
    })

  const onSearch = ({ searchTerms: newSearchTerms }) => {
    setSearchTerms(newSearchTerms)
  }

  if (isFetchError || isPaginationError)
    return (
      <>
        <JobSearch onSearchSubmit={onSearch} searchTerms={searchTerms} />
        <Notification theme="error">
          <div role="alert">Something went wrong.</div>
        </Notification>
      </>
    )

  if (isLoading)
    return (
      <>
        <JobSearch onSearchSubmit={onSearch} searchTerms={searchTerms} />
        <SkeletonJobList />
      </>
    )

  if (data.nodes.length === 0)
    return (
      <>
        <JobSearch onSearchSubmit={onSearch} searchTerms={searchTerms} />
        <EmptyListCard type={searchTerms ? JobListEmptyState.SEARCH : JobListEmptyState.MATCH} />
      </>
    )

  const onViewDetailsClick = ({ jobId, jobName, companyName, companyProfileSlug }) => {
    trackEvent('Candidate: position action', {
      action: 'clicked position',
      source: 'candidate position recommendations',
      position_id: jobId,
      position_name: jobName,
      company_name: companyName,
      company_id: companyProfileSlug,
      page_offset: paginationOffset,
      page_limit: paginationLimit,
      total_available: data.totalCount,
      search_string: !!searchTerms?.trim() ? searchTerms : null,
    })
  }

  return (
    <>
      <JobSearch onSearchSubmit={onSearch} searchTerms={searchTerms} />

      <div className={styles.listMetaData}>
        <h1 className={styles.heading}>
          {data.totalCount} job{data.totalCount > 1 ? 's' : ''} available{searchTerms ? ` for ${searchTerms}` : ''}
        </h1>
        <p className={styles.sortingText}>Sorted by relevance</p>
      </div>

      <ul className={styles.list}>
        <For each="job" of={data.nodes}>
          <li key={job.id}>
            <JobCard
              job={job}
              onViewDetailsClick={() =>
                onViewDetailsClick({
                  jobId: job.id,
                  jobName: job.name,
                  companyName: job.companyProfileName,
                  companyProfileSlug: job.companyProfileSlug,
                })
              }
            />
          </li>
        </For>
      </ul>

      {totalPages > 1 && (
        <Pagination className={styles.pagination} currentPage={currentPage} totalPages={totalPages} onPageSelect={setPage} />
      )}
    </>
  )
}

JobList.propTypes = {
  candidateProfileId: PropTypes.string,
}

export default JobList
